<template>
    <generic-page>
        <b-btn variant="link" size="lg" class="pl-0" to="/creator/call">
            <font-awesome-icon size="lg" icon="fa-light fa-chevron-left" />
            {{ $t("All Calls") }}
        </b-btn>

        <div v-if="call">
            <div @click="viewProfile">
                <user-standard-avatar
                    :standardMap="standardMap"
                    :nonPrimaryPhotos="false"
                    :hideBadges="true"
                    :userBetweenStatus="false"
                    size="small"
                    class="border-light border-top"
                />
            </div>

            <creator-call-dispute @refresh="refresh" :call="call" />

            <b-alert
                v-if="!call.dispute && call.escrow"
                variant="primary"
                show
                class="mt-2 small"
            >
                <font-awesome-icon icon="fa-duotone fa-info-circle" />
                Credits on hold will be transferred to you in
                <live-timestamp-future :timestamp="call.canBeDisputedUntil" />
                if the call remains undisputed

                <div class="mt-2">
                    <b-button
                        v-b-toggle.whyDelayed
                        variant="link"
                        class="text-secondary p-0"
                        size="sm"
                    >
                        Why is it delayed?
                    </b-button>
                    <b-collapse id="whyDelayed">
                        <div>
                            To prevent abuse, short calls less than
                            {{ config.creatorCallDisputeMinuteLimit }}
                            minutes can be disputed after the call finishes for
                            up to
                            {{ config.creatorCallReleaseEscrowMinutes }}
                            minutes by the user if they are unsatisfied.
                        </div>
                        <div class="mt-2">
                            Calls longer than
                            {{ config.creatorCallDisputeMinuteLimit }} minutes
                            will instantly transfer credits, there is no waiting
                            period and can never be disputed.
                        </div>
                    </b-collapse>
                </div>
            </b-alert>

            <div class="d-flex align-items-center justify-content-between mt-3">
                <h5 class="mb-0">{{ $t("Call Details") }}</h5>
                <b-btn
                    @click="earningsModal = true"
                    variant="light"
                    pill
                    size="sm"
                    class="border"
                >
                    <font-awesome-icon
                        icon="fa-duotone fa-info-circle"
                        class="mr-1"
                    />
                    {{ $t("How earnings work") }}
                </b-btn>
            </div>
            <b-list-group>
                <b-list-group-item
                    class="d-flex align-items-center justify-content-between"
                >
                    <div>Status</div>
                    <div class="small">
                        {{ call.status }}
                    </div>
                </b-list-group-item>
                <b-list-group-item
                    v-if="call.totalEarnings"
                    class="d-flex align-items-center justify-content-between"
                >
                    <div>Earnings</div>
                    <coin-badge :amount="call.totalEarnings" />
                </b-list-group-item>
                <b-list-group-item
                    v-if="call.escrow"
                    class="d-flex align-items-center justify-content-between"
                >
                    <div>Credits on Hold</div>
                    <font-awesome-icon
                        class="text-success"
                        icon="fa-light fa-check"
                    />
                </b-list-group-item>
                <b-list-group-item
                    v-if="call.duration"
                    class="d-flex align-items-center justify-content-between"
                >
                    <div>Duration</div>
                    <div class="small">
                        {{ call.duration }}
                    </div>
                </b-list-group-item>
            </b-list-group>
        </div>
        <div v-else>
            <generic-loading />
        </div>

        <b-modal
            v-model="earningsModal"
            hide-footer
            centered
            title="How Earnings Work"
        >
            <div class="mb-3">
                <strong>{{ $t("Why earnings might not match duration") }}</strong>
                <div class="small text-secondary">
                    {{
                        $t(
                            "Earnings won't always match the call length exactly. Here's how it works — and what to look for so you can earn the most from every call."
                        )
                    }}
                </div>
            </div>
            <div class="mb-3">
                <strong>
                    <font-awesome-icon
                        icon="fa-duotone fa-video"
                        class="mr-1 branded"
                    />
                    {{ $t("Active video stream required") }}
                </strong>
                <div class="small text-secondary">
                    {{
                        $t(
                            "You only earn while the system can detect an active video stream from you. If your camera turns off, your video freezes, or your connection drops, billing pauses until your stream is back. As soon as you reconnect, billing picks up again."
                        )
                    }}
                </div>
            </div>
            <div class="mb-3">
                <strong>
                    <font-awesome-icon
                        icon="fa-duotone fa-shield-check"
                        class="mr-1 branded"
                    />
                    {{ $t("Designed to be fair") }}
                </strong>
                <div class="small text-secondary">
                    {{
                        $t(
                            "This works in both directions. If a customer is being billed but no real video is coming back to them, or if your stream goes missing, the system will end the call automatically after a few minutes. It's there to prevent cheating on either side and protect your earnings."
                        )
                    }}
                </div>
            </div>
            <div class="mb-3">
                <strong>
                    <font-awesome-icon
                        icon="fa-duotone fa-eye"
                        class="mr-1 branded"
                    />
                    {{ $t("Watch earnings in real time") }}
                </strong>
                <div class="small text-secondary">
                    {{
                        $t(
                            "Your earnings update every minute during the call. Keep an eye on them — if they stop moving while you're still on camera, something is off. End the call and start a new one. A short clean call is better than a long broken one."
                        )
                    }}
                </div>
            </div>
            <div>
                <b-alert variant="warning" show class="small mb-0">
                    <font-awesome-icon
                        icon="fa-duotone fa-triangle-exclamation"
                        class="mr-1"
                    />
                    {{
                        $t(
                            "Phones lag. Wi-Fi drops packets. The internet has bad seconds. We've built the system to be as accurate as possible, but no live streaming system is 100% perfect. Use a strong Wi-Fi connection, sit somewhere well-lit, and stay clearly visible — that's how you earn for the time you're actually on camera."
                        )
                    }}
                </b-alert>
            </div>
            <b-btn
                @click="earningsModal = false"
                variant="primary"
                pill
                block
                class="mt-3"
            >
                {{ $t("Got it") }}
            </b-btn>
        </b-modal>
    </generic-page>
</template>

<script>
import { mapState } from "vuex";

import UserStandardAvatar from "@/components/user/UserStandardAvatar";
import CreatorCallDispute from "@/components/creator/call/CreatorCallDispute";

export default {
    data() {
        return {
            call: false,
            standardMap: false,
            earningsModal: false,
        };
    },
    mounted() {
        this.refresh();
    },
    watch: {
        $route(to) {
            if (to?.params?.id !== this.call?.id) {
                this.refresh();
            }
        },
    },
    computed: {
        ...mapState("options", ["config"]),
    },
    methods: {
        viewProfile() {
            this.$store.dispatch("profile/viewProfile", {
                userId: this.standardMap.id,
                profile: this.standardMap,
            });
        },
        refresh() {
            const id = this.$route.params?.id;

            this.api.post(`/call/${id}/get`).then((response) => {
                this.call = response?.data?.call;
                this.standardMap = response?.data?.standardMap;
            });
        },
    },
    components: {
        UserStandardAvatar,
        CreatorCallDispute,
    },
};
</script>